"use strict";
// Feature: address-geolocation, Task 4.1
//
// StubGeocodingProvider — a deterministic default `GeocodingProvider`
// implementation. Vendor selection is out of scope for the address-geolocation
// spec; this stub lets consumers wire the pluggable interface into their
// Lambdas today and swap in Nominatim/MapTiler/Google later without a stored-
// field change (Req 6.5, 8.3).
//
// Behaviour:
//   - Deterministic: the same address input always produces the same
//     `GeocodeResult` (or `null`). Powered by a stable string hash of the
//     structured address (or free-text) and a bounded coordinate space.
//   - Owner-agnostic: this is pure — the tenant boundary is enforced by the
//     Lambda that calls it, not by the adapter.
//   - `null` when the address has neither a postcode nor an
//     `addressLine1`/free-text hint; the caller then leaves state unchanged
//     per Req 6.4.
Object.defineProperty(exports, "__esModule", { value: true });
exports.StubGeocodingProvider = void 0;
/**
 * A cheap, deterministic 32-bit string hash. Not cryptographic — good enough
 * to spread across the lat/long space so equal inputs produce equal outputs
 * and different inputs land in different regions.
 */
function stableHash(input) {
    let h = 2166136261 >>> 0; // FNV-1a offset basis
    for (let i = 0; i < input.length; i++) {
        h ^= input.charCodeAt(i);
        h = Math.imul(h, 16777619) >>> 0;
    }
    return h >>> 0;
}
/** Map a 32-bit unsigned int onto [min, max) as a stable decimal. */
function scale(h, min, max) {
    const fraction = (h & 0xffffff) / 0xffffff; // 24-bit granularity
    return min + fraction * (max - min);
}
/**
 * Canonicalise an address-or-string into a hash key. Trims + lowercases each
 * field and joins with `\n` (won't naturally appear in postal fields).
 */
function canonicalise(input) {
    if (typeof input === "string")
        return input.trim().toLowerCase();
    const parts = [
        input.organisation,
        input.addressLine1,
        input.addressLine2,
        input.locality,
        input.town,
        input.county,
        input.postcode,
        input.countryCode,
    ];
    return parts
        .map((p) => (typeof p === "string" ? p.trim().toLowerCase() : ""))
        .join("\n");
}
/**
 * Does the input carry enough signal to be "resolvable" by the stub? Real
 * geocoders reject empty inputs — this stub does the same to keep test
 * expectations honest.
 */
function hasResolvableSignal(input) {
    if (typeof input === "string")
        return input.trim().length > 0;
    const hasPostcode = typeof input.postcode === "string" && input.postcode.trim().length > 0;
    const hasLine1 = typeof input.addressLine1 === "string" &&
        input.addressLine1.trim().length > 0;
    return hasPostcode || hasLine1;
}
/**
 * `StubGeocodingProvider` — a pure, deterministic `GeocodingProvider`.
 *
 * Given the same input, always returns the same `GeocodeResult`. Given an
 * input with no resolvable signal (empty string, or an `Address` missing
 * both `postcode` and `addressLine1`), returns `null`.
 *
 * The generated coordinates are within the UK bounding box by default
 * (roughly Land's End to John o'Groats), because Ledgiventa is UK-focussed.
 * Swap in a real provider when a vendor is chosen — no stored field changes.
 */
class StubGeocodingProvider {
    constructor(bounds) {
        // Default: UK bounding box (49.9 → 60.9, -8.7 → 1.8)
        this.bounds = bounds ?? {
            minLat: 49.9,
            maxLat: 60.9,
            minLng: -8.7,
            maxLng: 1.8,
        };
    }
    async geocode(input) {
        if (!hasResolvableSignal(input))
            return null;
        const key = canonicalise(input);
        // Two independent hashes so lat/long don't correlate on shared prefixes.
        const latHash = stableHash("lat:" + key);
        const lngHash = stableHash("lng:" + key);
        return {
            latitude: Number(scale(latHash, this.bounds.minLat, this.bounds.maxLat).toFixed(6)),
            longitude: Number(scale(lngHash, this.bounds.minLng, this.bounds.maxLng).toFixed(6)),
            // Stub accuracy — always the same for a stub; real providers vary.
            accuracyMetres: 50,
        };
    }
}
exports.StubGeocodingProvider = StubGeocodingProvider;
